import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import type {
  CompanyDetail,
  CompanyListItem,
  CreateCompanyPayload,
  PaginatedResponse,
  TimelineItem,
  UpdateCompanyPayload,
} from "../types";

const COMPANIES_KEY = ["companies"] as const;

export function useCompanyList(
  params: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    type?: string;
    category?: "OPERATIONAL" | "EXTERNAL";
    tab?: "operational" | "external";
    operationalOnly?: boolean;
    bypassScope?: boolean;
    parentId?: string;
  } = {},
): UseQueryResult<PaginatedResponse<CompanyListItem>> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...COMPANIES_KEY, "list", params, accessToken ? "auth" : "anon"],
    enabled: !!accessToken,
    queryFn: () =>
      apiFetch<PaginatedResponse<CompanyListItem>>("/companies", {
        token: accessToken,
        query: {
          page: params.page,
          limit: params.limit,
          search: params.search,
          status: params.status,
          type: params.type,
          category: params.category,
          tab: params.tab,
          operationalOnly: params.operationalOnly ? "true" : undefined,
          bypassScope: params.bypassScope ? "true" : undefined,
          parentId: params.parentId,
        },
      }),
  });
}

export function useCompany(id: string): UseQueryResult<CompanyDetail> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...COMPANIES_KEY, "detail", id, accessToken ? "auth" : "anon"],
    enabled: !!accessToken && !!id,
    queryFn: async () => {
      const res = await apiFetch<{ company: CompanyDetail }>(
        `/companies/${id}`,
        { token: accessToken },
      );
      return res.company;
    },
  });
}

export function useCreateCompany() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateCompanyPayload) =>
      apiFetch<{ company: CompanyDetail }>("/companies", {
        method: "POST",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...COMPANIES_KEY] });
    },
  });
}

export function useUpdateCompany(id: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateCompanyPayload) =>
      apiFetch<{ company: CompanyDetail }>(`/companies/${id}`, {
        method: "PUT",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...COMPANIES_KEY] });
    },
  });
}

export function useDeleteCompany() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/companies/${id}`, {
        method: "DELETE",
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...COMPANIES_KEY] });
    },
  });
}

export function useCompanySubEntity<T>(
  companyId: string,
  entity: string,
  params: Record<string, string | number | undefined> = {},
) {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...COMPANIES_KEY, companyId, entity, params, accessToken ? "auth" : "anon"],
    enabled: !!accessToken && !!companyId,
    queryFn: () =>
      apiFetch<PaginatedResponse<T>>(`/companies/${companyId}/${entity}`, {
        token: accessToken,
        query: params as Record<string, string | number>,
      }),
  });
}

export function useCompanyTimeline(
  companyId: string,
  params: {
    page?: number;
    limit?: number;
    types?: string;
    dateFrom?: string;
    dateTo?: string;
    search?: string;
  } = {},
) {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...COMPANIES_KEY, companyId, "timeline", params, accessToken ? "auth" : "anon"],
    enabled: !!accessToken && !!companyId,
    queryFn: () =>
      apiFetch<{ data: TimelineItem[]; total: number }>(
        `/companies/${companyId}/timeline`,
        {
          token: accessToken,
          query: {
            page: params.page,
            limit: params.limit,
            types: params.types,
            dateFrom: params.dateFrom,
            dateTo: params.dateTo,
            search: params.search,
          },
        },
      ),
  });
}
